import * as auditLogHelper from 'helpers/audit-log-helper';
import * as itemService from 'services/item-service';
import * as logger from 'logger';
import * as tokenService from 'services/token-service';
import { addParseAlert } from 'services/alert-service';
import { addPendingItem, buildPendingItem } from 'services/item-service';
import { Alert } from 'types/alert';
import { AppState } from 'pages/app/state';
import { AuditLogType } from 'types/audit-log-type';
import {
  DELETE_ALERT,
  DeleteAlertAction,
  LIST_ALERTS,
  ListAlertsAction,
  SetAlertsAction,
  RetryErroneousItemAction,
  RETRY_ERRONEOUS_ITEM,
  MANUAL_ENTRY,
  SCANDIT_ENTRY,
  ManualEntryAction,
  ScanditEntryAction,
  SCAN_ATTEMPT,
  ScanAttemptAction
} from './actions';
import { Middleware, MiddlewareAPI } from 'redux';
import { Epc } from 'types/epc';
import { Geot } from 'types/geot-details';
import { getGeot } from '../../helpers/geot-helpers';
import { EPC_SCAN_ACTION, ScanAction } from '../scanner/actions';
import { getDatabase } from 'database';
import { getI18n } from 'services/i18n-service';
import { NmvsActionType } from 'types/nmvs-action-type';
import { OperationItem } from 'types/operation-item';
import { parseCode } from '../scanner/scanner-helper';
import { syncData } from 'service-worker/data-syncs/data-sync';
import { translations } from 'types/translations';
import { AlertErrorKey } from 'types/alert-error-key';
import { tenantSelector } from 'pages/settings/selectors';
import { usernameSelector } from 'pages/user-profile/selectors';
import { EntryMode } from 'types/entry-mode';
import { currentOperationSelector } from 'components/menu/selectors';
import { AddOperationItemAction, SetOperationItemAction } from 'components/operation-grid/actions';
import { connectionSelector } from 'components/connection-status/selectors';

const MainMiddleware: Middleware = store => next => async action => {

  try {
    switch (action.type) {
      case LIST_ALERTS:
        await listAlerts(store);
        break;

      case DELETE_ALERT:
        await deleteAlert(store, action);
        break;

      case EPC_SCAN_ACTION:
        await processScan(store, action);
        break;

      case MANUAL_ENTRY:
        await processManualEntry(store, action);
        break;

      case SCANDIT_ENTRY:
        await processScanEntry(store, action);
        break;

      case SCAN_ATTEMPT:
        await onScanAttempt(store, action);
        break;

      case RETRY_ERRONEOUS_ITEM:
        await retryErroneousItem(store, action);
        break;

      default:
        break;
    }
    next(JSON.parse(JSON.stringify(action)));
  }
  catch (e) {
    const error = e as Error;
    logger.error(error, 'MainMiddleware');
  }
};

async function listAlerts(store: MiddlewareAPI) {
  try {
    // add to database
    const db = await getDatabase();
    const alerts: Alert[] = await db.readAll(db.config.indexes.Alert);
    store.dispatch(new SetAlertsAction(alerts));
  }
  catch (error) {
    throw new Error(error);
  }
}

async function deleteAlert(store: MiddlewareAPI, action: DeleteAlertAction) {
  try {
    // remove alert from database
    const db = await getDatabase();
    await db.remove(db.config.indexes.Alert, action.clientTransactionId);
    store.dispatch(new ListAlertsAction);

    const i18n = getI18n();
    const auditMessage = i18n.t(translations.AlertAcknowledgedAuditMessage, {
      username: usernameSelector(store.getState()),
      clientTransactionId: action.clientTransactionId
    });
    auditLogHelper.writeAuditLog(store, AuditLogType.Alert, 'AlertAcknowledged', '',
      auditMessage, undefined);
  }
  catch (error) {
    throw new Error(error);
  }
}

async function processManualEntry(store: MiddlewareAPI, action: ManualEntryAction) {
  await processOperation(store, action.epc, EntryMode.Manual,
    AuditLogType.ManualEntry, 'ManualEntry', 'mainMiddleware.processManualEntry', false, false);
}

async function processScanEntry(store: MiddlewareAPI, action: ScanditEntryAction) {
  await processOperation(store, action.epc, EntryMode.Manual,
    AuditLogType.ManualEntry, 'ScanditEntry', 'mainMiddleware.processManualEntry', false, false);
}

async function processScan(store: MiddlewareAPI, action: ScanAction) {
  await processOperation(store, action.epc, EntryMode.Automatic,
    AuditLogType.EpcScan, 'EpcScanned', 'mainMiddleware.processScan', action.isCapsLockOn, action.isSeparatorMissing);
}

async function onScanAttempt(store: MiddlewareAPI, action: ScanAttemptAction) {
  const i18n = getI18n();
  await addParseAlert(action.output, AlertErrorKey.ScannerConfig, i18n.t(translations.ScanAttemptDetected),
    i18n.t(translations.ScanAttemptSolution));
  store.dispatch(new ListAlertsAction());
  return;
}

async function processOperation(store: MiddlewareAPI, epcRawCode: string, entryMode: EntryMode,
  auditLogType: AuditLogType, auditLogAction: string, logLocation: string, isCapsLockOn: boolean, isSeparatorMissing: boolean) {
  const state: AppState = store.getState();
  const i18n = getI18n();
  let pendingItem: OperationItem;
  let epc: Epc;
  let geot: Geot;
  const currentOperationItem = currentOperationSelector(state);
  const currentOperation = currentOperationItem.nmvsOperation;

  if (connectionSelector(state) !== true &&
    (currentOperation === NmvsActionType.UndoDispense ||
      currentOperation === NmvsActionType.UndoSample)) {
    return;
  }

  // if (isCapsLockOn) {
  //   await addParseAlert(epcRawCode, AlertErrorKey.ScannerConfig, i18n.t(translations.CapsLockDetected),
  //     i18n.t(translations.CapsLockSolution));
  //   store.dispatch(new ListAlertsAction());
  //   return;
  // }

  // if (isSeparatorMissing) {
  //   await addParseAlert(epcRawCode, AlertErrorKey.ScannerConfig, i18n.t(translations.SeparatorMissing),
  //     i18n.t(translations.SeparatorSolution));
  //   store.dispatch(new ListAlertsAction());
  //   return;
  // }

  try {
    // parse the code and build pending item
    epc = await parseCode(epcRawCode);
  }
  catch (error) {
    logger.error(error, logLocation, epcRawCode);
    await addParseAlert(epcRawCode, AlertErrorKey.InvalidEpc, error.message, i18n.t(translations.EnterEpcManually));
    store.dispatch(new ListAlertsAction());
    return;
  }

  try {
    geot = await getGeot(usernameSelector(state));
  }
  catch (error) {
    return;
  }

  const auditMessage = i18n.t(translations.EpcScannedAuditMessage, {
    username: usernameSelector(state),
    operation: currentOperationItem.text,
    epc: epc.humanReadable
  });
  auditLogHelper.writeAuditLog(store, auditLogType, auditLogAction, NmvsActionType[currentOperation],
    auditMessage, connectionSelector(state));

  const operationName: string = i18n.t(NmvsActionType[currentOperation]);
  epc.status = i18n.t(translations.Pending) + ' ' + operationName.toLowerCase() + '...';

  const sessionToken = await tokenService.readToken(tokenService.JWT_SESSION_TOKEN);
  const tenant = tenantSelector(state);
  pendingItem = buildPendingItem(epc, geot, entryMode, currentOperationItem, tenant.countryCode, tenant.language, sessionToken);
  await handlePendingItem(pendingItem);
  store.dispatch(new AddOperationItemAction(pendingItem));
}

export async function handlePendingItem(pendingItem: OperationItem) {
  // add item to database
  const key = pendingItem.clientTransactionId.toString();
  await addPendingItem(key, pendingItem);
  syncData();
}

async function retryErroneousItem(store: MiddlewareAPI, action: RetryErroneousItemAction) {
  const item = action.operationItem;
  const i18n = getI18n();
  item.epc.status = i18n.t(translations.Pending) + ' ' + i18n.t(NmvsActionType[item.operation]).toLowerCase() + '...';
  item.isProcessing = false;
  item.hasError = false;
  item.scanDatetime = new Date().toISOString();
  item.optelResponseTime = 0;
  item.nmvsResponseTime = 0;
  item.totalResponseTime = 0;
  item.serverRequestStarted = '';
  await itemService.retryCompletedItem(action.operationItem);
  syncData();
  store.dispatch(new SetOperationItemAction(item.clientTransactionId, item));
}

export default MainMiddleware;


// WEBPACK FOOTER //
// ./src/components/main/middleware.ts